import type {
  HierarchyConflictDetails,
  ItemTypeAdminView,
  ItemTypeCreate,
  ItemTypePatch,
  ProductWorkflowSwitch,
  StatusAdminView,
  StatusCreate,
  StatusPatch,
  WorkflowAdminView,
  WorkflowCreate,
  WorkflowPatch,
} from "@plannello/shared";
import { queryOptions } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { queryClient } from "@/query-client";

export type { HierarchyConflictDetails, ItemTypeAdminView, StatusAdminView, WorkflowAdminView };

/**
 * Schema admin API: item types (apps/server/src/routes/itemTypes.ts) and workflows with statuses
 * (routes/workflows.ts). Every query lives under ["schema", "admin", ...] so the `schema.changed`
 * event of the live stream refreshes it together with the catalog.
 */

const V1 = "/api/v1";

export const itemTypesQuery = queryOptions({
  queryKey: ["schema", "admin", "item-types"],
  queryFn: ({ signal }) => api.get<ItemTypeAdminView[]>(`${V1}/item-types`, signal),
  staleTime: 30_000,
});

export const workflowsQuery = queryOptions({
  queryKey: ["schema", "admin", "workflows"],
  queryFn: ({ signal }) => api.get<WorkflowAdminView[]>(`${V1}/workflows`, signal),
  staleTime: 30_000,
});

export type StatusUsage = { workflowId: string; statuses: (StatusAdminView & { itemCount: number })[] };

/** The statuses a product's items use (deleted and archived items included): rows of the switch mapping. */
export const statusUsageQuery = (productKey: string) =>
  queryOptions({
    queryKey: ["schema", "admin", "status-usage", productKey.toUpperCase()],
    queryFn: ({ signal }) =>
      api.get<StatusUsage>(`${V1}/products/${encodeURIComponent(productKey)}/status-usage`, signal),
    staleTime: 0,
    gcTime: 0,
  });

const refreshSchema = () => queryClient.invalidateQueries({ queryKey: ["schema"] });

/** Everything that shows names, categories or counts of statuses and types, after the product's workflow changed. */
const refreshAll = () => {
  for (const key of ["spaces", "item", "lites", "search"]) void queryClient.invalidateQueries({ queryKey: [key] });
  return refreshSchema();
};

async function write<T>(run: () => Promise<T>): Promise<T> {
  try {
    return await run();
  } finally {
    void refreshSchema();
  }
}

// ---------------------------------------------------------------------------------------------------
// Item types

const setTypes = (fn: (all: ItemTypeAdminView[]) => ItemTypeAdminView[]) =>
  queryClient.setQueryData<ItemTypeAdminView[]>(itemTypesQuery.queryKey, (old) => (old ? fn(old) : old));

export const createItemType = (input: ItemTypeCreate) =>
  write(() => api.post<ItemTypeAdminView>(`${V1}/item-types`, input));

export const updateItemType = (id: string, patch: ItemTypePatch) =>
  write(() => api.patch<ItemTypeAdminView>(`${V1}/item-types/${id}`, patch));

export const setItemTypeArchived = (id: string, archived: boolean) =>
  write(() => api.post<ItemTypeAdminView>(`${V1}/item-types/${id}/${archived ? "archive" : "unarchive"}`));

export const deleteItemType = (id: string) =>
  write(() => api.delete<{ id: string; deleted: true }>(`${V1}/item-types/${id}`));

/** Applies the order at once; the caller refetches on failure. */
export function reorderItemTypes(typeIds: string[]) {
  const pos = new Map(typeIds.map((id, i) => [id, i]));
  setTypes((all) => all.map((t) => (pos.has(t.id) ? { ...t, position: pos.get(t.id)! } : t)));
  return write(() => api.post(`${V1}/item-types/reorder`, { typeIds }));
}

export interface ItemTypeMigrateResult {
  fromTypeId: string;
  toTypeId: string;
  dryRun: boolean;
  total: number;
  moved: number;
  movedDeleted: number;
  skippedCount: number;
  skipped: { id: string; key: string; code: string; message: string }[];
}

export const migrateItemType = (id: string, input: { toTypeId: string; dryRun?: boolean; query?: string }) =>
  write(() => api.post<ItemTypeMigrateResult>(`${V1}/item-types/${id}/migrate`, input));

// ---------------------------------------------------------------------------------------------------
// Workflows and statuses (mutations answer with the updated workflow view)

const setWorkflow = (view: WorkflowAdminView) =>
  queryClient.setQueryData<WorkflowAdminView[]>(workflowsQuery.queryKey, (old) =>
    old ? old.map((w) => (w.id === view.id ? view : w)) : old,
  );

/** Edits the cached workflow before the server answers (drag and drop must not wait for the network). */
export function patchWorkflowCache(id: string, fn: (w: WorkflowAdminView) => WorkflowAdminView) {
  queryClient.setQueryData<WorkflowAdminView[]>(workflowsQuery.queryKey, (old) =>
    old ? old.map((w) => (w.id === id ? fn(w) : w)) : old,
  );
}

export const createWorkflow = (input: WorkflowCreate) =>
  write(() => api.post<WorkflowAdminView>(`${V1}/workflows`, input));

export const updateWorkflow = async (id: string, patch: WorkflowPatch) => {
  const view = await write(() => api.patch<WorkflowAdminView>(`${V1}/workflows/${id}`, patch));
  setWorkflow(view);
  return view;
};

export const deleteWorkflow = (id: string) =>
  write(() => api.delete<{ id: string; deleted: true }>(`${V1}/workflows/${id}`));

export const createStatus = async (workflowId: string, input: StatusCreate) => {
  const view = await write(() => api.post<WorkflowAdminView>(`${V1}/workflows/${workflowId}/statuses`, input));
  setWorkflow(view);
  return view;
};

export const updateStatus = async (statusId: string, patch: StatusPatch) => {
  const view = await write(() => api.patch<WorkflowAdminView>(`${V1}/statuses/${statusId}`, patch));
  setWorkflow(view);
  return view;
};

export const reorderStatuses = async (workflowId: string, statusIds: string[]) => {
  const view = await write(() =>
    api.post<WorkflowAdminView>(`${V1}/workflows/${workflowId}/statuses/reorder`, { statusIds }),
  );
  setWorkflow(view);
  return view;
};

export type StatusRemoved = { movedItems: number; movedToStatusId: string | null; workflow: WorkflowAdminView };

export const removeStatus = async (statusId: string, moveItemsTo?: string) => {
  const q = moveItemsTo ? `?moveItemsTo=${encodeURIComponent(moveItemsTo)}` : "";
  const res = await write(() => api.delete<StatusRemoved>(`${V1}/statuses/${statusId}${q}`));
  setWorkflow(res.workflow);
  return res;
};

export const switchProductWorkflow = async (productKey: string, input: ProductWorkflowSwitch) => {
  try {
    return await api<{ workflow: WorkflowAdminView }>(`${V1}/products/${encodeURIComponent(productKey)}/workflow`, {
      method: "PUT",
      body: input,
    });
  } finally {
    void refreshAll();
  }
};
